import { create } from 'zustand';

export interface ErrorToast {
  id: string;
  statusCode: number;
  message: string;
  timestamp: number;
}

interface ErrorToastState {
  toasts: ErrorToast[];
  addToast: (statusCode: number, message: string) => void;
  removeToast: (id: string) => void;
  clearAll: () => void;
}

/**
 * Global error toast system for displaying API errors.
 *
 * ## Automatic Usage
 * All axios and React Query errors are automatically displayed as toasts.
 * No additional code needed in components.
 *
 * ## Opt-out (for custom error handling)
 * ```typescript
 * try {
 *   await farmsApi.create(data)
 * } catch (error) {
 *   // Prevent global toast
 *   if (error.config) {
 *     error.config.metadata = { skipGlobalErrorToast: true }
 *   }
 *   // Show custom error UI
 *   setLocalError("Custom message")
 * }
 * ```
 *
 * ## Manual toast (for non-API errors)
 * ```typescript
 * import { useErrorToastStore } from "@/shared/stores/errorToastStore"
 *
 * const { addToast } = useErrorToastStore()
 * addToast(500, "Custom error message")
 * ```
 */
export const useErrorToastStore = create<ErrorToastState>((set) => ({
  toasts: [],

  addToast: (statusCode: number, message: string) => {
    const toast: ErrorToast = {
      id: `error-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      statusCode,
      message,
      timestamp: Date.now(),
    };

    set((state) => ({
      // Add new toast at the beginning, keep max 5 toasts
      toasts: [toast, ...state.toasts].slice(0, 5),
    }));
  },

  removeToast: (id: string) => {
    set((state) => ({
      toasts: state.toasts.filter((toast) => toast.id !== id),
    }));
  },

  clearAll: () => {
    set({ toasts: [] });
  },
}));
